import React, { useState } from "react";
import axios from "axios";
import { Dialog, DialogContent } from "./ui/dialog";
import { X, Check, Loader2 } from "lucide-react";
import { useEnquiry } from "../context/EnquiryContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initialState = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  wedding_date: "",
  wedding_venue: "",
  hear_about: "",
  service: "",
  message: ""
};

const HEAR_OPTIONS = [
  "Google",
  "Instagram",
  "Facebook",
  "TikTok",
  "Referral / Word of mouth",
  "Our venue recommended you",
  "Wedding fair",
  "Hitched / Bridebook",
  "Other"
];

const SERVICE_OPTIONS = [
  "Wedding Videography",
  "Wedding Photography",
  "Photo & Video Together",
  "Not sure yet. Tell us about both"
];

const EnquiryDialog = () => {
  const { isOpen, close } = useEnquiry();
  const [form, setForm] = useState(initialState);
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("submitting");
    setErrorMsg("");
    try {
      await axios.post(`${API}/enquiries`, form);
      setStatus("success");
      setForm(initialState);
    } catch (err) {
      setStatus("error");
      setErrorMsg(err?.response?.data?.detail || "Something went wrong. Please try again or email us directly.");
    }
  };

  const handleClose = () => {
    close();
    // Allow modal close animation before resetting state
    setTimeout(() => {
      if (status === "success" || status === "error") {
        setStatus("idle");
        setErrorMsg("");
      }
    }, 400);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent
        className="max-w-2xl w-[calc(100vw-2rem)] max-h-[92vh] overflow-y-auto p-0 bg-[#f5efe6] border-0 sm:rounded-none"
      >
        {/* Close */}
        <button
          onClick={handleClose}
          aria-label="Close"
          className="absolute top-5 right-5 z-10 p-2 text-[#1f1d1a] hover:text-[#8b6f47] transition-colors"
        >
          <X size={22} />
        </button>

        {status === "success" ? (
          <div className="px-8 md:px-14 py-16 text-center">
            <div className="w-16 h-16 mx-auto rounded-full border-2 border-[#c9a87c] flex items-center justify-center mb-8">
              <Check size={28} className="text-[#8b6f47]" />
            </div>
            <p className="kicker text-[#8b6f47] mb-5">Thanks!</p>
            <h3 className="font-display display-tight text-4xl md:text-5xl text-[#1f1d1a] mb-6">
              Your enquiry<br/><span className="italic font-light">is on its way.</span>
            </h3>
            <p className="text-[#3a342c] font-light leading-relaxed max-w-md mx-auto">
              We’ll get back to you within 24 hours. Usually much sooner. In the meantime, feel free to have a stalk on Instagram.
            </p>
            <button onClick={handleClose} className="btn-primary mt-10">
              Back to the site
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="px-7 md:px-12 py-10 md:py-14">
            <p className="kicker text-[#8b6f47] mb-4">Enquire</p>
            <h3 className="font-display display-tight text-4xl md:text-5xl text-[#1f1d1a] mb-3">
              Let’s talk about<br/><span className="italic font-light">your wedding.</span>
            </h3>
            <p className="text-[#3a342c]/80 text-sm font-light mb-8">
              Pop your details in below and we’ll be back to you within 24 hours.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="First name *" name="first_name" value={form.first_name} onChange={handleChange} required />
              <Field label="Last name" name="last_name" value={form.last_name} onChange={handleChange} />
              <Field label="Email *" type="email" name="email" value={form.email} onChange={handleChange} required />
              <Field label="Phone *" type="tel" name="phone" value={form.phone} onChange={handleChange} required />
              <Field label="Wedding date *" type="date" name="wedding_date" value={form.wedding_date} onChange={handleChange} required />
              <Field label="Wedding venue" name="wedding_venue" value={form.wedding_venue} onChange={handleChange} placeholder="e.g. Gaynes Park" />
              <SelectField label="How did you hear about us?" name="hear_about" value={form.hear_about} onChange={handleChange} options={HEAR_OPTIONS} />
              <SelectField label="Interested in *" name="service" value={form.service} onChange={handleChange} options={SERVICE_OPTIONS} required />
            </div>

            <div className="mt-5">
              <label className="block text-[0.7rem] tracking-[0.25em] uppercase text-[#8b6f47] mb-2">Anything else?</label>
              <textarea
                name="message"
                rows={4}
                value={form.message}
                onChange={handleChange}
                placeholder="Tell us a little about your day..."
                className="w-full bg-transparent border border-[#c9b893] text-[#1f1d1a] font-light px-4 py-3 focus:outline-none focus:border-[#1f1d1a] transition-colors text-sm resize-none"
              />
            </div>

            {status === "error" && (
              <p className="mt-5 text-sm text-red-700 font-light">{errorMsg}</p>
            )}

            <button
              type="submit"
              disabled={status === "submitting"}
              className="btn-primary mt-8 w-full justify-center disabled:opacity-60"
            >
              {status === "submitting" ? (
                <><Loader2 size={16} className="animate-spin" /> Sending…</>
              ) : (
                <>Send Enquiry</>
              )}
            </button>

            <p className="mt-5 text-[0.7rem] tracking-[0.2em] uppercase text-[#8b6f47] text-center">
              No pressure. No spam. Just a reply within 24 hours.
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, name, type = "text", value, onChange, required, placeholder }) => (
  <div>
    <label htmlFor={name} className="block text-[0.7rem] tracking-[0.25em] uppercase text-[#8b6f47] mb-2">{label}</label>
    <input
      id={name}
      name={name}
      type={type}
      value={value}
      onChange={onChange}
      required={required}
      placeholder={placeholder}
      className="w-full bg-transparent border border-[#c9b893] text-[#1f1d1a] font-light px-4 py-3 focus:outline-none focus:border-[#1f1d1a] transition-colors text-sm"
    />
  </div>
);

const SelectField = ({ label, name, value, onChange, options, required }) => (
  <div>
    <label htmlFor={name} className="block text-[0.7rem] tracking-[0.25em] uppercase text-[#8b6f47] mb-2">{label}</label>
    <select
      id={name}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      className="w-full bg-transparent border border-[#c9b893] text-[#1f1d1a] font-light px-4 py-3 focus:outline-none focus:border-[#1f1d1a] transition-colors text-sm appearance-none cursor-pointer"
    >
      <option value="">Choose an option…</option>
      {options.map((o) => (
        <option key={o} value={o}>{o}</option>
      ))}
    </select>
  </div>
);

export default EnquiryDialog;
